import * as z from 'zod/v4';

import { DEFAULT_DRAW_ID } from './constants.js';

export const drawNumberSchema = z.coerce.number().int().nonnegative().brand<'DrawNumber'>();

/**
 * Branded type for draw number.
 */
export type DrawNumber = z.infer<typeof drawNumberSchema>;

/**
 * Utility function to create a DrawNumber.
 *
 * @param value input value to check
 * @returns returns true or false if the value matches pattern for draw number.
 */
export function isDrawNumber(value: unknown): value is DrawNumber {
  if (typeof value !== 'number') {
    return false;
  }

  if (!Number.isInteger(value)) {
    return false;
  }

  return true;
}

/**
 * Check if a value is the default draw number.
 *
 * @param value input value to check
 * @returns returns true if the value is the default draw number, false otherwise.
 */
export function isDefaultDrawNumber(value: unknown): value is typeof DEFAULT_DRAW_ID {
  return value === DEFAULT_DRAW_ID;
}

/**
 * Utility function to create an DrawNumber.
 *
 * @param value input value to check
 * @returns Throws error if input dosen't match draw number, otherwise returns the event id with branded type.
 * @throws {Error} if input dosen't match draw number.
 */
export function getDrawNumber(value: unknown): DrawNumber {
  if (isDefaultDrawNumber(value)) {
    throw new Error('Default draw number is not a valid draw number');
  }

  const parsedValue = typeof value === 'string' && value.length > 0 ? Number.parseInt(value, 10) : value;

  if (!isDrawNumber(parsedValue)) {
    throw new Error('Invalid DrawNumber');
  }

  return parsedValue;
}
